import axios from '@/services/axios'

export type StoreStatus = 'draft' | 'active' | 'suspended'

export type AdminStore = {
	id: number
	uuid: string
	slug: string
	name: string
	status: StoreStatus
	description?: string | null
	tagline?: string | null
	logo_url?: string | null
	cover_url?: string | null
	address?: string | null
	city?: string | null
	zip?: string | null
	country?: string | null
	lat?: number | string | null
	lng?: number | string | null
	phone?: string | null
	email?: string | null
	instagram?: string | null
	working_hours?: Record<string, unknown> | null
	paypal_client_id?: string | null
	paypal_mode?: string | null
	locafox_tenant_id?: string | null
	locafox_username?: string | null
	locafox_store_id?: number | null
	/** Shared platform SevDesk base URL (same for every store) */
	sevdesk_url?: string | null
	invoice_counter?: number | null
	has_paypal_secret?: boolean
	has_locafox_password?: boolean
	has_sevdesk_api_key?: boolean
	onboarding?: { complete: boolean; missing: string[] }
	admins?: Array<{
		user_id: number
		email?: string
		first_name?: string
		last_name?: string
		is_owner?: boolean
	}>
}

export type StoreUpsertPayload = {
	name: string
	slug?: string
	status?: StoreStatus
	description?: string
	tagline?: string
	address?: string
	city?: string
	zip?: string
	country?: string
	lat?: number | string | null
	lng?: number | string | null
	geocode?: boolean
	phone?: string
	email?: string
	instagram?: string
	paypal_client_id?: string
	paypal_client_secret?: string
	paypal_mode?: string
	locafox_tenant_id?: string
	locafox_username?: string
	locafox_password?: string
	/** Locafox location id inside the tenant (default 1) */
	locafox_store_id?: number | string
	/** Next invoice number for this store */
	invoice_counter?: number | string
	sevdesk_api_key?: string
}

const listStores = async (): Promise<AdminStore[]> => {
	const response = await axios.get('/stores')
	return response.data.data
}

const getStore = async (uuid: string): Promise<AdminStore> => {
	const response = await axios.get(`/stores/admin/${uuid}`)
	return response.data.data
}

const createStore = async (data: StoreUpsertPayload): Promise<AdminStore> => {
	const response = await axios.post('/stores', data)
	return response.data.data
}

const updateStore = async (uuid: string, data: Partial<StoreUpsertPayload>): Promise<AdminStore> => {
	const response = await axios.put(`/stores/${uuid}`, data)
	return response.data.data
}

const setStatus = async (uuid: string, status: StoreStatus): Promise<AdminStore> => {
	const response = await axios.patch(`/stores/${uuid}/status`, { status })
	return response.data.data
}

const assignAdmin = async (
	uuid: string,
	data: { email: string; password?: string; first_name?: string; last_name?: string },
): Promise<AdminStore> => {
	const response = await axios.post(`/stores/${uuid}/admins`, data)
	return response.data.data
}

const removeAdmin = async (uuid: string, userId: number): Promise<AdminStore> => {
	const response = await axios.delete(`/stores/${uuid}/admins/${userId}`)
	return response.data.data
}

export type PublicStore = {
	uuid: string
	slug: string
	name: string
	status: StoreStatus
	description?: string | null
	tagline?: string | null
	logo_url?: string | null
	cover_url?: string | null
	address?: string | null
	city?: string | null
	zip?: string | null
	country?: string | null
	lat?: number | string | null
	lng?: number | string | null
	phone?: string | null
	email?: string | null
	instagram?: string | null
	working_hours?: Record<string, Array<{ start: string; end: string }>> | null
	paypal_client_id?: string | null
	paypal_mode?: string | null
}

export type StoreProfilePayload = {
	description?: string
	tagline?: string
	logo_url?: string
	cover_url?: string
	address?: string
	city?: string
	zip?: string
	country?: string
	lat?: number | string | null
	lng?: number | string | null
	geocode?: boolean
	phone?: string
	email?: string
	instagram?: string
	working_hours?: Record<string, Array<{ start: string; end: string }>> | null
	invoice_counter?: number | string
}

const searchStores = async (params: Record<string, string>): Promise<PublicStore[]> => {
	const query = new URLSearchParams(params).toString()
	const response = await axios.get(`/stores/search?${query}`)
	return response.data.data
}

const getPublicStore = async (ref: string): Promise<PublicStore> => {
	const response = await axios.get(`/stores/${encodeURIComponent(ref)}`)
	return response.data.data
}

const listMyStores = async (): Promise<AdminStore[]> => {
	const response = await axios.get('/stores/mine')
	return response.data.data
}

const updateMyStoreProfile = async (
	uuid: string,
	data: StoreProfilePayload,
): Promise<PublicStore> => {
	const response = await axios.put(`/stores/mine/${uuid}/profile`, data)
	return response.data.data
}

const storeService = {
	listStores,
	getStore,
	createStore,
	updateStore,
	setStatus,
	assignAdmin,
	removeAdmin,
	searchStores,
	getPublicStore,
	listMyStores,
	updateMyStoreProfile,
}

export default storeService
